import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService, RenewedToken, SHOP_RENEWAL_PATH } from '@app/auth/services/auth.service';
import { ApiUrlService } from '@core/services/common/api-url.service';
import { LocalStorageService } from '@shared/services/storage';


/**
 * ZADANIE B5.4. Interceptor uwierzytelniania sklepu: dokłada token do żądań do NASZEGO API
 * i zamienia odpowiedź 401 na odnowienie sesji zamiast cichego wylogowania.
 *
 * DLACZEGO TRAFIŁ DO MODUŁU GŁÓWNEGO. Serwisy wołające API koszyka i zamówień
 * (`ApiBasketService`, `ApiOrderService`) są dostarczane w module współdzielonym, włączanym
 * przez `SharedModule.forRoot()` — czyli z wstrzykiwacza GŁÓWNEGO. Dostawały więc klienta
 * z łańcucha głównego, a token dokładały sobie ręcznie, przez co odpowiedź 401 na tych
 * trasach nie uruchamiała odnawiania sesji. Stąd przeniesienie.
 *
 * ALE ŁAŃCUCH LENIWY NIE JEST MARTWY — i pierwsza wersja tego opisu twierdziła inaczej,
 * co kosztowało regresję. Moduły konta i koszyka importują własny `HttpClientModule`, co
 * tworzy DRUGI, osobny łańcuch interceptorów, a `ApiUserService` jest dostarczany wprost
 * w ich `providers`, więc dostaje klienta właśnie stamtąd. Zdjęcie rejestracji z tamtych
 * modułów zostawiło jego żądania — zmianę hasła, dane adresowe, historię zamówień — BEZ
 * nagłówka autoryzacji, bo ten serwis nie dokłada go sam. Interceptor jest dlatego
 * zarejestrowany w OBU miejscach, a strzeże tego
 * `src/app/modules/ecommerce/lazy-modules-auth-wiring.spec.ts`.
 *
 * Wejście do łańcucha głównego wymagało trzech straży, bo tym łańcuchem idzie WSZYSTKO:
 *
 * 1. SPRAWDZENIE POCHODZENIA — bez niego nasz token pojechałby do każdego adresu, z którym
 *    aplikacja się kontaktuje, w tym do obcych serwisów z mediami;
 * 2. USTĘPOWANIE ISTNIEJĄCEMU NAGŁÓWKOWI — część serwisów dokłada autoryzację sama, a nagłówki
 *    tej samej nazwy się w żądaniu KUMULUJĄ, więc bez tej straży takie żądanie dostałoby
 *    dwa nagłówki naraz;
 * 3. WYŁĄCZENIE KONWERSJI NAZW dla samego odnowienia — w łańcuchu głównym stoi interceptor
 *    zamieniający nazwy pól na wielbłądzią notację, który zmieniłby `access_token`
 *    na `accessToken`. Odnowienie wyglądałoby na udane, a zapisany token byłby niezdefiniowany.
 *
 * Po tym łańcuchu chodzą też niezalogowani (koszyk), więc 401 gościa nie może uruchamiać
 * odnawiania sesji, której nie ma.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private localStorageService: LocalStorageService,
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private authService: AuthService,
  ) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.localStorageService.getItem('token');
    const copiedReq = this.czyDolozycToken(req, accessToken)
      ? req.clone({setHeaders: {Authorization: 'Bearer ' + accessToken}})
      : req;

    return next.handle(copiedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Bez tokenu w magazynie nie ma czego odnawiać — 401 jest wtedy zwykłą odmową,
        // a nie wygaśnięciem sesji. Token czytamy ponownie, bo równoległe żądanie mogło
        // w tym czasie zamknąć sesję.
        if (error.status !== 401
          || !this.localStorageService.getItem('token')
          || req.url.includes(SHOP_RENEWAL_PATH)) {
          return throwError(() => error);
        }

        // catchError musi łapać wyłącznie błąd samego odnowienia — jeśli token się
        // odnowił, a ponowione żądanie i tak dostanie inny błąd (np. 403, 500), to
        // jest błąd biznesowy tego żądania, nie powód do wylogowania kogoś ze
        // świeżym tokenem. Stąd catchError przed switchMap, nie po.
        return this.renew().pipe(
          catchError((renewalError) => {
            // Sesji nie da się już przedłużyć. Razem z magazynem zerujemy stan
            // usługi — bez tego interfejs pokazywałby zalogowanego użytkownika
            // aż do przeładowania strony.
            this.authService.clearSession();
            return throwError(() => renewalError);
          }),
          // `setHeaders` zamienia nagłówek, a nie dokłada drugiego — ponowienie musi
          // pójść ze ŚWIEŻYM tokenem także wtedy, gdy pierwotne żądanie niosło własny,
          // ręcznie dołożony i już nieważny.
          switchMap((renewed) => next.handle(
            req.clone({setHeaders: {Authorization: `Bearer ${renewed.access_token}`}}),
          )),
        );
      }),
    );
  }

  /**
   * Straż 1 i 2 z opisu klasy. Token dokładamy tylko wtedy, gdy jest, gdy żądanie idzie
   * do NASZEGO pochodzenia i gdy nie niesie już własnego nagłówka autoryzacji.
   *
   * Adres względny liczy się jako własne pochodzenie — konstruktor adresu rozwiązuje go
   * względem adresu strony.
   */
  private czyDolozycToken(req: HttpRequest<any>, accessToken: string | null): boolean {
    if (!accessToken) {
      return false;
    }

    if (req.headers.has('Authorization')) {
      return false;
    }

    return this.czyWlasnePochodzenie(req.url);
  }

  private czyWlasnePochodzenie(adres: string): boolean {
    const adresApi = this.apiUrlService.getApiUrl();

    if (adres.startsWith(adresApi)) {
      return true;
    }

    try {
      return new URL(adres, window.location.origin).origin === window.location.origin;
    } catch {
      // Adres, którego nie da się rozwiązać, traktujemy jako obcy — odmowa dołożenia
      // tokenu jest tu mniejszym złem niż oddanie go nieznanemu adresowi.
      return false;
    }
  }

  /**
   * Odnowienie należy do usługi uwierzytelniania, a nie do tego interceptora.
   *
   * Wcześniej strumień odnowienia żył TUTAJ i było to jedyne miejsce w sklepie, które
   * potrafiło przedłużyć sesję. Odkąd sklep odnawia ją także przy starcie i przy powrocie
   * do karty, dwa właścicielskie strumienie oznaczałyby wyścig: ewidencja sesji pamięta dwa
   * identyfikatory tokenu, więc dwa odnowienia w locie wypychają okazany token z pary
   * i jedno z nich kończy się odmową. Jeden właściciel usuwa ten wyścig — kto zapyta drugi,
   * wisi na tej samej odpowiedzi. Zapis magazynu i stanu usługi też jest już tamtejszy.
   */
  private renew(): Observable<RenewedToken> {
    return this.authService.renewSession();
  }
}
